/**
 * voice-v8 — Werkzeugkarten: Fehlerkarte und Karte für ein Werkzeug, das noch im Hintergrund läuft.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import type { ToolCardMessage } from "../webrtc.js";
import { baueKarte, chips, fehlerBlock, hinweisKarte, kartenTitel, leerHinweis } from "../karte.js";
import { toolLabel, trimText, unwrapResult } from "./hilfe.js";

// 2026-07-03 (Martin): haeufige Technik-Fehler menschlich uebersetzen.
// Leerstring = kein Match -> Originaltext zeigen.
function friendlyError(tool: string, raw: string): string {
  const t = (raw + " " + tool).toLowerCase();
  if (/duffel|\b422\b|flight|flug/.test(t))
    return "Die Flugsuche hat gerade nicht geklappt — prüf kurz Orte und Datum, oder versuch's gleich nochmal.";
  if (/credit\s*balance|insufficient|quota|\b402\b|payment\s*required/.test(t))
    return "Ein Dienst hat gerade kein Guthaben — ich geb Martin Bescheid.";
  if (/timeout|timed\s*out|etimedout|econnrefused|network|fetch\s*failed/.test(t))
    return "Die Verbindung war kurz weg — bitte gleich nochmal.";
  if (/zeitzone|timezone|\bzone\b/.test(t))
    return "Kleiner Zeitzonen-Hänger — ich hab's mit Wien-Zeit versucht.";
  if (/\b401\b|\b403\b|unauthorized|forbidden|not.?logged/.test(t))
    return "Zugriff war kurz gesperrt — bitte neu anmelden oder gleich nochmal.";
  if (/\b404\b|not[-\s]?found/.test(t))
    return "Nicht gefunden — vielleicht gibt es das (noch) nicht.";
  if (/\b429\b|rate\s*limit|overloaded/.test(t))
    return "Kurz zu viel los — bitte in ein paar Sekunden nochmal.";
  return "";
}

export function buildErrorCard(msg: ToolCardMessage): HTMLElement {
  // Pre.786: kein roher JSON.stringify mehr als Sichttext — Fehlertext
  // sauber extrahieren, Rest eingeklappt als Rohdaten (Stufe-6-Muster).
  const r = unwrapResult(msg.result);
  const SOFT_FAIL_TOOLS = new Set([
    "user_location_get",
    "route_directions",
    "show_map",
    "weather",
  ]);
  if (SOFT_FAIL_TOOLS.has(msg.tool)) {
    const soft =
      typeof r.error === "string" && r.error ? r.error : "nicht verfügbar";
    return hinweisKarte(`${toolLabel(msg.tool)}: ${trimText(soft, 80)}`);
  }
  const el = baueKarte("error");
  el.appendChild(kartenTitel(`${toolLabel(msg.tool)} · Fehler`));
  const errText =
    typeof r.error === "string" && r.error
      ? r.error
      : typeof msg.result === "string"
        ? msg.result
        : "Tool fehlgeschlagen";
  /*
   * ⚠️ 20.09.2026 — HIER STAND EIN MASCHINENCODE IM CHAT.
   *
   *    Martin, mit vier Karten untereinander:
   *        Auftrag an die Webseite · Fehler
   *        auftrag-laenge
   *
   *    Das Werkzeug liefert den guten Satz laengst mit — tools/webseiten.ts
   *    setzt `speak: "Der Auftrag ist zu kurz — sag in einem Satz, was sich
   *    aendern soll."` und unterscheidet sogar zu kurz von zu lang.
   *    `fehlerAntwort()` dort sagt es ausdruecklich: „Der Satz des Panels
   *    gewinnt — er ist genauer als jede Tabelle hier."
   *
   *    Die Karte fragte ihn nur nicht. Sie nahm `error`, reichte ihn durch
   *    `friendlyError` — eine Tabelle fuer TECHNISCHE Fehler (HTTP-Codes,
   *    Netz, Guthaben) — und zeigte bei einem unbekannten Code den Code.
   *    Eine Uebersetzungstabelle hinkt neuen Codes immer hinterher; der Satz
   *    am Werkzeug nicht.
   *
   * ⚠️ DAS WIDERSPRICHT NICHT DER REGEL VOM 18.09. Dort wurde `speak` als
   *    Sicht-Fallback beim ERFOLG entfernt, und das war richtig: neben einer
   *    Liste von drei Webseiten sagt „3 Webseiten." nichts. Auf einer
   *    FEHLERKARTE steht daneben gar nichts — die Alternative ist ein Code.
   *
   * Reihenfolge: der Satz des Werkzeugs, dann die Tabelle fuer technische
   * Fehler, dann als Letztes der Rohtext.
   */
  const eigenerSatz =
    typeof (r as { speak?: unknown }).speak === "string" &&
    (r as { speak: string }).speak.trim()
      ? (r as { speak: string }).speak.trim()
      : "";
  const friendly = eigenerSatz || friendlyError(msg.tool, errText);
  el.appendChild(fehlerBlock(friendly || trimText(errText, 300)));
  // 15.10.2026: kein „Rohdaten anzeigen“ mehr unter dem Satz — Martin sah den Knopf in Karte um Karte.
  return el;
}


// Pre.851 (#136): Deferred-Card — Tool läuft noch im Hintergrund.
// Reused von essence_mirror, human_design, human_design_connection.
export function buildDeferredCard(msg: ToolCardMessage): HTMLElement {
  const r = unwrapResult(msg.result);
  const label =
    msg.tool === "human_design_connection"
      ? "HD Verbindung"
      : toolLabel(msg.tool);
  const el = baueKarte("deferred", label);
  // 14.10.2026: der Stand als Pille, der Satz als leise Zeile — aus dem Grundgerüst,
  // kein eigenes Pulsieren rechts außen mehr.
  chips(el, ["läuft"]);
  leerHinweis(
    el,
    trimText(
      typeof r.speak === "string" && r.speak.length > 0
        ? r.speak
        : "Läuft im Hintergrund — schau gleich in der App nach.",
      200,
    ),
  );
  return el;
}
